import { useState, useMemo, useCallback, useRef, useEffect, type ReactNode } from "react";
import { ExternalLink, ChevronDown, Eye, Loader2, Sparkles, SquareCheckBig, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  ALL_EVIDENCE_TYPES,
  ALL_SOURCE_TYPES,
  ALL_TAB,
  buildSourceTypeTabs,
  countForTab,
  isSelectableOwnership,
  matchesSourceTypeTab,
} from "@/components/evidenceGathering/documentFilters";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import { useNetsuiteClientById } from "@/hooks/useMasterData";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Document, DocumentTopic, OWNERSHIP_VALUES } from "@/types/models";
import type { DocumentUpdatePayload } from "@/hooks/useDocumentMetadata";
import { useDeleteEvidenceDocument } from "@/hooks/useDeleteEvidenceDocument";
import { useMetadataQueue } from "@/hooks/useMetadataQueue";
import { AgGridReact } from "ag-grid-react";
import { ColDef, FilterChangedEvent, GridApi, GridReadyEvent, SelectionChangedEvent } from "ag-grid-community";
import * as DocumentToolbar from "@/components/DocumentToolbar";
import ResetFiltersButton from "@/components/ResetFiltersButton";
import DeleteDocumentDialog from "@/components/DeleteDocumentDialog";
import DocumentMetadataFields, { DocumentMetadataFieldsHandle, MetadataFormValues } from "@/components/DocumentMetadataFields";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import "@/lib/ag-grid-setup";
import { DocumentMetaBlock } from "@/components/DocumentMetaBlock";
import { formatDataRetention } from "@/lib/formatDataRetention";


interface Props {
  documents: Document[];
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onUpdateDocument?: (
    payload: { id: string } & DocumentUpdatePayload,
    options?: { onSuccess?: () => void }
  ) => void;
  isUpdating?: boolean;
  showEvidenceBaseLink?: boolean;
  showCheckboxes?: boolean;
  projectDocumentIds?: string[];
  onSelectionChange?: (count: number, documentIds: string[]) => void;
  projectId?: string;
  projectModel?: string;
  toolbarActions?: ReactNode;
  /** Stretch the grid to fill remaining parent height instead of using a fixed height */
  fillHeight?: boolean;
  showDocumentTabs?: boolean;
  pageTitle?: string;
  /** Show the Ownership filter dropdown (All / Private / Client). When false, no ownership filtering is applied internally. */
  showOwnershipFilter?: boolean;
  /** When true (default), Client and legacy Public ownership are read-only - changing either moves the document between projects. */
  lockSetOwnership?: boolean;
  /** Restrict which ownership values appear in the edit dialog dropdown. Defaults to OWNERSHIP_VALUES (Private, Client). */
  allowedOwnershipValues?: string[];
}

const WrappedTextCellRenderer = (params: { value: string }) => (
  <div className="py-2 leading-snug whitespace-normal break-words">{params.value}</div>
);

const SourceTypeCellRenderer = (params: { value: string }) => {
  const colorMap: Record<string, string> = {
    Guideline: "border-brand-teal text-brand-teal",
    Regulatory: "border-brand-amber text-brand-amber",
    HTA: "border-status-pico text-status-pico",
  };
  const cls = colorMap[params.value] || "border-muted-foreground text-muted-foreground";
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 border ${cls}`}>
      {params.value}
    </span>
  );
};

const toTitleCase = (str: string) =>
  str.split(/\s+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

const TagsCellRenderer = (params: { value: string[] }) => {
  const tags = Array.isArray(params.value) ? params.value : [];
  if (tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 py-1.5">
      {tags.map((tag) => (
        <Badge key={tag} variant="secondary" className="text-xs font-normal">{toTitleCase(tag)}</Badge>
      ))}
    </div>
  );
};

const TopicsCellRenderer = (params: { value: DocumentTopic[] }) => {
  const topics = Array.isArray(params.value) ? params.value : [];
  if (topics.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 py-1.5">
      {topics.map((topic) => (
        <Badge key={topic.id} variant="secondary" className="text-xs font-normal">{topic.name}</Badge>
      ))}
    </div>
  );
};

function documentToFormValues(doc: Document): MetadataFormValues {
  return {
    title: doc.title,
    authors: doc.author,
    year: doc.year,
    description: doc.description,
    source_type: doc.sourceType,
    organization: doc.sourceOrganisation,
    country: doc.country || "",
    evidence_type: doc.evidenceType,
    data_retention: doc.dataRetention,
    url: doc.url || "",
    language: doc.language || "",
    ownership: doc.ownership || "Private",
    client_id: doc.clientId || "",
    client_name: "",
    tags: doc.tags.join(", "),
    topic_ids: (doc.topics ?? []).map((t) => t.id),
  };
}

const EvidenceGathering = ({
  documents,
  isLoading = false,
  isError = false,
  error = null,
  onUpdateDocument,
  isUpdating = false,
  showEvidenceBaseLink = false,
  showCheckboxes = false,
  projectDocumentIds,
  onSelectionChange,
  projectId,
  projectModel,
  toolbarActions,
  fillHeight = false,
  showDocumentTabs = false,
  pageTitle = "Evidence Base",
  showOwnershipFilter = false,
  lockSetOwnership = true,
  allowedOwnershipValues,
}: Props) => {
  const { user } = useAuth();

  const canEditDocument = useCallback((doc: Document) => {
    return user?.role === 'Admin' || (doc.createdBy != null && doc.createdBy === user?.sub);
  }, [user]);
  const [activeSourceType, setActiveSourceType] = useState(ALL_TAB);
  const [searchQuery, setSearchQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterSourceType, setFilterSourceType] = useState(ALL_SOURCE_TYPES);
  const [filterEvidenceType, setFilterEvidenceType] = useState(ALL_EVIDENCE_TYPES);
  const [filterOwnership, setFilterOwnership] = useState(ALL_TAB);
  const [editingDoc, setEditingDoc] = useState<Document | null>(null);
  const { deletingDoc, setDeletingDoc, deleteStage, handleDelete, errorMessage: deleteErrorMessage } = useDeleteEvidenceDocument(projectId, projectDocumentIds);
  const persistedSelectionRef = useRef<Set<string>>(new Set(projectDocumentIds ?? []));
  // This project's projects_documents rows - the private scope: a document is
  // admitted to exactly the projects it has a link for.
  const [linkedIds, setLinkedIds] = useState<Set<string>>(new Set(projectDocumentIds ?? []));
  const { openDocument } = useDocumentViewer();

  const gridApiRef = useRef<GridApi | null>(null);
  const [hasGridColumnFilters, setHasGridColumnFilters] = useState(false);

  const queue = useMetadataQueue();

  // On mount: resume polling if a metadata extraction was in progress for a document
  useEffect(() => {
    const stored = sessionStorage.getItem("evidence_gathering_metadata_extraction");
    if (!stored) return;
    try {
      const { document_id } = JSON.parse(stored) as { document_id: string };
      if (!document_id) return;
      // Single doc status check — O(1)
      queue.startPolling([document_id], (statuses) => {
        const info = statuses[document_id];
        if (info?.status === "Done" && info.result) {
          editForm.reset({
            ...editForm.getValues(),
            title: info.result.title || "",
            authors: info.result.author || "",
            year: info.result.year || "",
            description: info.result.description || "",
            source_type: info.result.source_type || "",
            organization: info.result.source_organisation || "",
            country: info.result.country || "",
            evidence_type: info.result.evidence_type || "",
            url: info.result.url || "",
            language: info.result.language || "",
            tags: info.result.tags?.join(", ") || "",
          });
        }
        sessionStorage.removeItem("evidence_gathering_metadata_extraction");
      });
    } catch {
      sessionStorage.removeItem("evidence_gathering_metadata_extraction");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editForm = useForm<MetadataFormValues>();
  const metadataFieldsRef = useRef<DocumentMetadataFieldsHandle>(null);

  const openEditDialog = useCallback((docId: string) => {
    const doc = documents.find(d => d.id === docId);
    if (!doc) return;
    editForm.reset(documentToFormValues(doc));
    setEditingDoc(doc);
  }, [editForm, documents]);

  useEffect(() => {
    if (editingDoc) {
      editForm.setValue("ownership", editingDoc.ownership || "Private");
      editForm.setValue("client_id", editingDoc.clientId || "");
    }
  }, [editingDoc, editForm]);

  const { data: resolvedEditClient } = useNetsuiteClientById(
    editingDoc?.clientId && !editForm.getValues("client_name") ? editingDoc.clientId : null
  );
  useEffect(() => {
    if (resolvedEditClient) {
      editForm.setValue("client_name", resolvedEditClient.name);
    }
  }, [resolvedEditClient, editForm]);

  const handleEditSave = (data: MetadataFormValues) => {
    if (!editingDoc) return;
    if (metadataFieldsRef.current?.focusInvalidField()) return;
    onUpdateDocument?.(
      {
        id: editingDoc.id,
        filename: editingDoc.filename || null,
        title: data.title || null,
        authors: data.authors || null,
        year: data.year || null,
        description: data.description || null,
        source_type: data.source_type || null,
        organization: data.organization || null,
        country: data.country || null,
        evidence_type: data.evidence_type || null,
        data_retention: data.data_retention || null,
        url: data.url || null,
        language: data.language || null,
        ownership: data.ownership || null,
        client_id: data.ownership === "Client" ? (data.client_id || null) : null,
        tags: data.tags || null,
        topic_ids: data.topic_ids,
      },
      {
        onSuccess: () => setEditingDoc(null),
      }
    );
  };

  const handleExtractMetadata = useCallback(async () => {
    if (!editingDoc?.id || !editingDoc?.filename) return;
    const documentId = editingDoc.id;
    setIsEnqueuing(true);
    try {
      await queue.enqueue([{
        document_id: documentId,
        filename: editingDoc.filename,
        model: projectModel,
      }]);
      // Persist for resume on navigate-away
      sessionStorage.setItem(
        "evidence_gathering_metadata_extraction",
        JSON.stringify({ document_id: documentId }),
      );
      queue.startPolling([documentId], (statuses) => {
        const info = statuses[documentId];
        if (info?.status === "Done" && info.result) {
          editForm.reset({
            ...editForm.getValues(),
            title: info.result.title || "",
            authors: info.result.author || "",
            year: info.result.year || "",
            description: info.result.description || "",
            source_type: info.result.source_type || "",
            organization: info.result.source_organisation || "",
            country: info.result.country || "",
            evidence_type: info.result.evidence_type || "",
            url: info.result.url || "",
            language: info.result.language || "",
            tags: info.result.tags?.join(", ") || "",
          });
        }
        sessionStorage.removeItem("evidence_gathering_metadata_extraction");
      });
    } catch {
      // enqueue failed — silently reset polling state
    } finally {
      setIsEnqueuing(false);
    }
  }, [editingDoc, queue, editForm, projectModel]);

  // Legacy ownership (Public, unset) stays hidden unless this project links it -
  // never drop a document out from under a live project.
  const selectable = useMemo(
    () => documents.filter((d) => isSelectableOwnership(d) || linkedIds.has(d.id)),
    [documents, linkedIds]
  );

  const sourceTypes = useMemo(() => [ALL_SOURCE_TYPES, ...Array.from(new Set(selectable.filter((d) => d.sourceType).map((d) => d.sourceType)))], [selectable]);
  const evidenceTypes = useMemo(() => [ALL_EVIDENCE_TYPES, ...Array.from(new Set(selectable.filter((d) => d.evidenceType).map((d) => d.evidenceType)))], [selectable]);

  // Every filter except the source-type tab. The grid and the tab counts both
  // read this, so a count cannot disagree with the rows.
  const visible = useMemo(() => {
    let result = selectable;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          d.author.toLowerCase().includes(q) ||
          d.description.toLowerCase().includes(q) ||
          d.sourceOrganisation.toLowerCase().includes(q) ||
          d.filename.toLowerCase().includes(q) ||
          d.tags.some((t) => t.toLowerCase().includes(q)) ||
          d.topics.some((t) => t.name.toLowerCase().includes(q))
      );
    }

    if (filterSourceType !== ALL_SOURCE_TYPES) {
      result = result.filter((d) => d.sourceType === filterSourceType);
    }
    if (filterEvidenceType !== ALL_EVIDENCE_TYPES) {
      result = result.filter((d) => d.evidenceType === filterEvidenceType);
    }
    if (showOwnershipFilter && filterOwnership !== ALL_TAB) {
      result = result.filter((d) => d.ownership?.toLowerCase() === filterOwnership.toLowerCase());
    }

    return result;
  }, [selectable, searchQuery, filterSourceType, filterEvidenceType, showOwnershipFilter, filterOwnership]);

  const sourceTypeTabs = useMemo(() => buildSourceTypeTabs(visible), [visible]);

  const sourceTypeCount = useCallback((sourceType: string) => countForTab(visible, sourceType), [visible]);

  const filtered = useMemo(() => {
    const rows = activeSourceType === ALL_TAB
      ? visible
      : visible.filter((d) => matchesSourceTypeTab(d, activeSourceType));

    if (showOwnershipFilter && filterOwnership === ALL_TAB) {
      return [...rows].sort((a, b) => {
        const aPrivate = a.ownership?.toLowerCase() === "private" ? 0 : 1;
        const bPrivate = b.ownership?.toLowerCase() === "private" ? 0 : 1;
        return aPrivate - bPrivate;
      });
    }

    return rows;
  }, [visible, activeSourceType, showOwnershipFilter, filterOwnership]);

  const [isEnqueuing, setIsEnqueuing] = useState(false);
  const [tableSelectedCount, setTableSelectedCount] = useState(0);
  const allSelected = filtered.length > 0 && tableSelectedCount === filtered.length;

  const handleSelectAll = () => {
    const api = gridApiRef.current;
    if (!api) return;
    if (allSelected) {
      api.deselectAll();
    } else {
      api.selectAll();
    }
  };

  // Re-applies persistedSelectionRef to currently visible rows without touching hidden rows
  const reapplyPersistedSelection = useCallback(() => {
    const api = gridApiRef.current;
    if (!api || !showCheckboxes) return;
    api.forEachNode((node) => {
      const id = node.data?.id;
      if (id) node.setSelected(persistedSelectionRef.current.has(id), false, "api");
    });
  }, [showCheckboxes]);

  // Called when projectDocumentIds arrives from API — resets persisted selection to match
  const applyPreSelection = useCallback(() => {
    const api = gridApiRef.current;
    if (!api || !showCheckboxes || !projectDocumentIds) return;
    persistedSelectionRef.current = new Set(projectDocumentIds);
    setLinkedIds(new Set(projectDocumentIds));
    reapplyPersistedSelection();
  }, [showCheckboxes, projectDocumentIds, reapplyPersistedSelection]);

  // Re-apply when projectDocumentIds changes (e.g. after initial API load)
  useEffect(() => {
    applyPreSelection();
  }, [applyPreSelection]);

  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    filterSourceType !== ALL_SOURCE_TYPES ||
    filterEvidenceType !== ALL_EVIDENCE_TYPES ||
    (showOwnershipFilter && filterOwnership !== ALL_TAB) ||
    hasGridColumnFilters;

  const handleResetFilters = useCallback(() => {
    gridApiRef.current?.setFilterModel(null);
    setSearchQuery("");
    setFilterSourceType(ALL_SOURCE_TYPES);
    setFilterEvidenceType(ALL_EVIDENCE_TYPES);
    setFilterOwnership(ALL_TAB);
    setHasGridColumnFilters(false);
  }, []);

  // A filter can empty the active tab; fall back to All rather than show nothing.
  useEffect(() => {
    if (sourceTypeTabs.includes(activeSourceType)) return;
    setActiveSourceType(ALL_TAB);
  }, [sourceTypeTabs, activeSourceType]);

  const onFilterChanged = useCallback((event: FilterChangedEvent) => {
    const filterModel = event.api.getFilterModel();
    setHasGridColumnFilters(Object.keys(filterModel).length > 0);
  }, []);

  const columnDefs = useMemo<ColDef<Document>[]>(() => [
    ...(showCheckboxes ? [{
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 50,
      maxWidth: 50,
      suppressHeaderMenuButton: true,
      sortable: false,
      filter: false,
    } as ColDef<Document>] : []),
    {
      headerName: "Title",
      field: "title",
      flex: 2,
      minWidth: 250,
      autoHeight: true,
      cellRenderer: (params: { data: Document }) => (
        <div className="py-2">
          {canEditDocument(params.data) ? (
            <button
              type="button"
              className="block font-medium text-brand-teal hover:underline text-left leading-snug whitespace-normal break-words"
              onClick={() => openEditDialog(params.data.id)}
            >
              {params.data.title}
            </button>
          ) : (
            <span className="block font-medium text-foreground text-left leading-snug whitespace-normal break-words">
              {params.data.title}
            </span>
          )}
          <DocumentMetaBlock
            filename={params.data.filename || undefined}
            authors={params.data.author || undefined}
            countries={params.data.country || undefined}
            year={params.data.year || undefined}
          />
        </div>
      ),
    },
    {
      headerName: "Country",
      field: "country",
      flex: 0.8,
      minWidth: 100,
      autoHeight: true,
      cellRenderer: WrappedTextCellRenderer,
    },
    {
      headerName: "Source Type",
      field: "sourceType",
      flex: 1,
      minWidth: 120,
      autoHeight: true,
      cellRenderer: SourceTypeCellRenderer,
    },
    { headerName: "Tags", field: "tags", flex: 1.5, minWidth: 180, autoHeight: true, cellRenderer: TagsCellRenderer },
    { headerName: "Topics", field: "topics" as keyof Document, flex: 1.5, minWidth: 180, autoHeight: true, cellRenderer: TopicsCellRenderer },
    { headerName: "Year", field: "year", flex: 0.5, minWidth: 80 },
    { headerName: "Evidence Type", field: "evidenceType", flex: 1, minWidth: 120, autoHeight: true, cellRenderer: WrappedTextCellRenderer },
    {
      headerName: "Data Retention",
      field: "dataRetention",
      flex: 0.8,
      minWidth: 100,
      valueFormatter: (params: { value: string | null }) => formatDataRetention(params.value),
      filterValueGetter: (params) => formatDataRetention(params.data?.dataRetention),
    },
    {
      headerName: "Ownership",
      field: "ownership",
      flex: 0.8,
      minWidth: 90,
      cellRenderer: (params: { data: Document }) => {
        const val = params.data.ownership;
        if (!val) return null;
        const colours: Record<string, string> = {
          Public: "bg-green-100 text-green-800",
          Client: "bg-blue-100 text-blue-800",
          Private: "bg-gray-100 text-gray-600",
        };
        const cls = colours[val] ?? "bg-gray-100 text-gray-600";
        return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{val}</span>;
      },
    },
    {
      headerName: "",
      colId: "actions",
      width: 80,
      maxWidth: 80,
      sortable: false,
      filter: false,
      resizable: false,
      suppressHeaderMenuButton: true,
      cellRenderer: (params: { data: Document }) => (
        <div className="flex items-center justify-center h-full gap-1">
          <button
            type="button"
            title="View original PDF"
            onClick={() => openDocument({ doc: params.data, showTabs: showDocumentTabs })}
            className="p-1.5 rounded text-muted-foreground hover:text-brand-teal hover:bg-brand-teal/10 transition-colors"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          {canEditDocument(params.data) && (
            <button
              type="button"
              title="Delete document"
              onClick={() => setDeletingDoc(params.data)}
              className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ),
    } as ColDef<Document>,
  ], [showCheckboxes, openEditDialog, setDeletingDoc, openDocument, showDocumentTabs, canEditDocument]);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    filter: true,
    resizable: true,
  }), []);

  const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    const selectedRows = event.api.getSelectedRows();
    const selectedInView = new Set(selectedRows.map((r: Document) => r.id));
    // Update persisted selection for currently visible rows only; hidden rows keep their state
    event.api.forEachNode((node) => {
      const id = node.data?.id;
      if (!id) return;
      if (selectedInView.has(id)) {
        persistedSelectionRef.current.add(id);
      } else {
        persistedSelectionRef.current.delete(id);
      }
    });
    setLinkedIds(new Set(persistedSelectionRef.current));
    setTableSelectedCount(selectedRows.length);
    onSelectionChange?.(persistedSelectionRef.current.size, Array.from(persistedSelectionRef.current));
  }, [onSelectionChange]);

  const SelectDropdown = ({
    label,
    value,
    options,
    onChange,
  }: {
    label: string;
    value: string;
    options: string[];
    onChange: (v: string) => void;
  }) => (
    <div className="flex-1 min-w-0">
      <label className="block text-sm font-semibold text-foreground mb-1.5">{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none border rounded-md px-3 py-2.5 pr-8 text-sm bg-card focus:outline-none focus:ring-2 focus:ring-brand-teal/30 cursor-pointer"
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      </div>
    </div>
  );

  return (
    <div className={fillHeight ? "flex flex-col flex-1 min-h-0" : undefined}>
      {showEvidenceBaseLink && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-foreground">{pageTitle}</h2>
            <p className="text-sm text-muted-foreground">Documents tagged to this project from the evidence base</p>
          </div>
          {/* Hidden: to restore remove the "hidden" class below */}
          <Link
            to="/evidence"
            className="hidden flex items-center gap-1.5 text-sm font-medium text-brand-teal hover:underline"
          >
            <ExternalLink className="w-4 h-4" />
            Open Evidence Base
          </Link>
        </div>
      )}

      <DocumentToolbar.Root>
        <DocumentToolbar.Search value={searchQuery} onChange={setSearchQuery} />
        <DocumentToolbar.FiltersToggle
          open={filtersOpen}
          active={hasActiveFilters}
          onToggle={() => setFiltersOpen((prev) => !prev)}
        />
        {toolbarActions}
      </DocumentToolbar.Root>
      <DocumentToolbar.FilterPanel open={filtersOpen}>
        <SelectDropdown label="Source Type" value={filterSourceType} options={sourceTypes} onChange={setFilterSourceType} />
        <SelectDropdown label="Evidence Type" value={filterEvidenceType} options={evidenceTypes} onChange={setFilterEvidenceType} />
        {showOwnershipFilter && (
          <SelectDropdown label="Ownership" value={filterOwnership} options={[ALL_TAB, ...OWNERSHIP_VALUES]} onChange={setFilterOwnership} />
        )}
        <div className="flex items-end">
          <ResetFiltersButton hasActiveFilters={hasActiveFilters} onReset={handleResetFilters} />
        </div>
      </DocumentToolbar.FilterPanel>

      {/* Source Type tabs */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1 bg-[#f1f5f9] p-1">
          {sourceTypeTabs.map((type) => (
            <button
              key={type}
              onClick={() => setActiveSourceType(type)}
              className={`text-sm px-3 py-1.5 rounded font-medium transition-colors ${
                activeSourceType === type ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {type} ({sourceTypeCount(type)})
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {showCheckboxes && (
            <>
              <span className="inline-block min-w-[140px] text-sm text-center text-muted-foreground">{tableSelectedCount} of {filtered.length} selected</span>

              <Button 
              onClick={handleSelectAll} 
              variant="outline"
              >
                <SquareCheckBig className="w-4 h-4"  />
                Select All
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Loading / Error states */}
      {isLoading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading documents...
        </div>
      )}
      {isError && (
        <div className="py-8 text-center text-sm text-destructive">
          Failed to load documents: {error?.message}
        </div>
      )}

      {/* AG Grid */}
      {!isLoading && !isError && (
        <div className={`ag-theme-alpine border overflow-hidden${fillHeight ? " flex-1 min-h-0" : ""}`} style={fillHeight ? undefined : { height: 500 }}>
          <AgGridReact<Document>
            theme="legacy"
            rowData={filtered}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowSelection="multiple"
            onGridReady={(e: GridReadyEvent) => { gridApiRef.current = e.api; }}
            onRowDataUpdated={reapplyPersistedSelection}
            onSelectionChanged={onSelectionChanged}
            onFilterChanged={onFilterChanged}
            animateRows={true}
            headerHeight={44}
            suppressRowClickSelection={true}
            getRowId={(params) => params.data.id}
          />
        </div>
      )}



      {/* Delete Confirmation Dialog */}
      <DeleteDocumentDialog
        deletingDoc={deletingDoc}
        deleteStage={deleteStage}
        errorMessage={deleteErrorMessage}
        onCancel={() => setDeletingDoc(null)}
        onConfirm={handleDelete}
      />


      {/* Edit Document Metadata Dialog */}
      <Dialog open={!!editingDoc} onOpenChange={(open) => { if (!open) setEditingDoc(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Document Metadata</DialogTitle>
            <DialogDescription>
              {editingDoc?.filename}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={editForm.handleSubmit(handleEditSave)}>
            <DocumentMetadataFields
              ref={metadataFieldsRef}
              register={editForm.register}
              control={editForm.control}
              getValues={editForm.getValues}
              setValue={editForm.setValue}
              idPrefix="edit"
              readOnlyOwnership={lockSetOwnership && ["public", "client"].includes(editingDoc?.ownership?.toLowerCase() ?? "")}
              allowedOwnershipValues={allowedOwnershipValues}
            />
            {editingDoc && queue.documentStatuses[editingDoc.id]?.status === "Error" && (
              <p className="text-sm text-destructive mt-3">
                AI extraction failed: {queue.documentStatuses[editingDoc.id]?.error ?? "Unknown error"}
              </p>
            )}
            <div className="flex items-center justify-between gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handleExtractMetadata}
                disabled={isEnqueuing || queue.isPolling || isUpdating}
                className="gap-1.5"
              >
                {isEnqueuing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Queued...</>
                ) : queue.isPolling ? (() => {
                  const docId = editingDoc?.id;
                  const info = docId ? queue.documentStatuses[docId] : undefined;
                  const status = info?.status;
                  if (status === "Queued") {
                    const pos = info?.queue_position;
                    return <><Loader2 className="w-4 h-4 animate-spin" /> Queued{pos ? ` (${pos} of ${queue.queueSize})` : ""}...</>;
                  }
                  return <><Loader2 className="w-4 h-4 animate-spin" /> Extracting...</>;
                })() : (
                  <><Sparkles className="w-4 h-4" /> Extract with AI</>
                )}
              </Button>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setEditingDoc(null)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-brand-teal text-white hover:bg-brand-teal/90 gap-1.5"
                  disabled={isUpdating || queue.isPolling}
                >
                  {isUpdating ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EvidenceGathering;
